import type { InvestorHolding, InvestorHoldingsResponse } from '../types/api';
import { chainDisplayName } from '@/lib/chain-display-name';
import { formatCompactUsd } from '@/lib/format/usd/compact-usd';
import {
  formatTokenUnits,
  formatHoldingUnitPrice,
  formatHoldingValue,
  holdingDisplayName,
  KIND_LABELS,
  priceSourceLabel,
  tierLabel,
  type CurrencyRegistry,
  type HoldingsValuation
} from './holding-format';
import type { LensOption } from '@/components/lens-panel';
import { collapseTail } from '@/lib/collapse-tail';
import type { PortfolioDonutSegment } from '@/components/portfolio-donut';

// The Positions card's row model: which bucket a holding falls in under
// each lens, and the donut segments and table groups built from them. Pure,
// so the card itself only holds state and markup.

export type PositionLens = 'position' | 'asset-class' | 'type';

/**
 * "Account" from the Figma lens set has no backend concept yet (Safe support
 * is pending), so that lens decomposes by holding type instead — a real,
 * decision-useful split of the same reconciled scope.
 *
 * There is no Chain lens. The holdings contract is scoped to one chain and
 * the schema rejects any row from another, so that lens could only ever
 * show a single bucket at 100%.
 */
export const POSITION_LENSES: readonly LensOption<PositionLens>[] = [
  { value: 'position', label: 'Position' },
  { value: 'asset-class', label: 'Asset class' },
  { value: 'type', label: 'Type' }
];

interface LedgerEntry extends PortfolioDonutSegment {
  /** US dollars, converted. What segments are ordered and summed by. */
  value: number;
}

/**
 * Everything the card needs to know about its rows, worked out once: a
 * unique id per row, and the response's valuation (`valueHoldings`): each
 * row's value in its own currency, its dollar conversion when a rate is
 * served, and its share of the total on the same basis as that total.
 */
export interface RowFacts {
  ids: Map<InvestorHolding, string>;
  valuation: HoldingsValuation | null;
  currencies: CurrencyRegistry | undefined;
  fx: InvestorHoldingsResponse['fx'];
}

export const idOf = (facts: RowFacts, holding: InvestorHolding) =>
  facts.ids.get(holding)!;
export const valueOf = (facts: RowFacts, holding: InvestorHolding) =>
  facts.valuation?.values.get(holding) ?? null;
/** The row's dollar value, or null when it cannot join a dollar total. */
export const usdOf = (facts: RowFacts, holding: InvestorHolding) =>
  valueOf(facts, holding)?.usd ?? null;
/** The row's share of the stated total, in percent, or null. */
export const shareOf = (facts: RowFacts, holding: InvestorHolding) =>
  facts.valuation?.shares.get(holding) ?? null;

export const usdLabel = (value: number) =>
  formatCompactUsd(value, { maximumFractionDigits: 2 });

/** The donut segment (and table group) a holding belongs to per lens. */
export function segmentIdFor(
  holding: InvestorHolding,
  lens: PositionLens,
  facts: RowFacts
): string {
  switch (lens) {
    case 'position':
      return idOf(facts, holding);
    case 'type':
      return holding.kind;
    case 'asset-class':
      return holding.assetClass?.slug ?? 'unclassified';
  }
}

function groupLabelFor(holding: InvestorHolding, lens: PositionLens): string {
  switch (lens) {
    case 'position':
      return holdingDisplayName(holding);
    case 'type':
      return KIND_LABELS[holding.kind];
    case 'asset-class':
      return (
        holding.assetClass?.displayName ??
        holding.assetClass?.slug ??
        'Unclassified'
      );
  }
}

/**
 * Composition segments for the donut: grouped by lens, tail collapsed.
 *
 * Only rows with a dollar value take part. A ROWA row is stated in its
 * vault's strategy currency, so it is converted through the served rate
 * before it is compared with or added to anything; a row with no rate is
 * left out rather than counted one for one, and the card says so. Each
 * single holding is still labelled in its own currency.
 */
export function buildEntries(
  rows: InvestorHolding[],
  lens: PositionLens,
  facts: RowFacts
): LedgerEntry[] {
  const inDollars = rows.filter((holding) => usdOf(facts, holding) != null);

  const memberEntry = (holding: InvestorHolding): LedgerEntry => ({
    id: idOf(facts, holding),
    label: holdingDisplayName(holding),
    detail: `${KIND_LABELS[holding.kind]} · ${chainDisplayName(holding.chainId)}`,
    value: usdOf(facts, holding)!,
    valueLabel: formatHoldingValue(valueOf(facts, holding)),
    share: shareOf(facts, holding) ?? 0,
    facts: [
      { label: 'Units', value: formatTokenUnits(holding) },
      {
        label: 'Unit price',
        value: `${formatHoldingUnitPrice(holding, facts.currencies, facts.fx)} · ${priceSourceLabel(holding)}`
      },
      ...(lens === 'position' && holding.liquidityTier
        ? [{ label: 'Liquidity', value: tierLabel(holding.liquidityTier) }]
        : [])
    ]
  });

  let grouped: LedgerEntry[];
  if (lens === 'position') {
    grouped = inDollars.map(memberEntry);
  } else {
    const groups = new Map<
      string,
      { label: string; value: number; share: number; children: LedgerEntry[] }
    >();
    for (const holding of inDollars) {
      const key = segmentIdFor(holding, lens, facts);
      // The sunburst's outer ring: each member nests inside its bucket.
      const child = memberEntry(holding);
      const existing = groups.get(key);
      if (existing) {
        existing.value += child.value;
        existing.share += child.share;
        existing.children.push(child);
      } else {
        groups.set(key, {
          label: groupLabelFor(holding, lens),
          value: child.value,
          share: child.share,
          children: [child]
        });
      }
    }
    grouped = [...groups.entries()].map(([key, group]) => ({
      id: key,
      label: group.label,
      detail: `${group.children.length} holding${group.children.length === 1 ? '' : 's'}`,
      value: group.value,
      // A bucket adds rows up, so it is always a dollar figure.
      valueLabel: usdLabel(group.value),
      share: group.share,
      children: group.children.sort((left, right) => right.value - left.value)
    }));
  }

  grouped.sort((left, right) => right.value - left.value);

  return collapseTail(grouped, (tail) => {
    const value = tail.reduce((sum, entry) => sum + entry.value, 0);
    return {
      id: 'other',
      label: 'Other holdings',
      detail: `${tail.length} smaller holdings`,
      value,
      valueLabel: usdLabel(value),
      share: tail.reduce((sum, entry) => sum + entry.share, 0)
    };
  });
}

export interface LedgerGroup {
  id: string;
  label: string;
  /** Dollar value of the members that have one. */
  value: number;
  /** How many members that value covers. */
  valuedCount: number;
  share: number;
  members: InvestorHolding[];
}

/**
 * The table's first layer under a grouped lens: one row per lens bucket,
 * every served holding included (unpriced members, and members with no
 * exchange rate, ride along outside the dollar value), ordered by value
 * like the donut.
 */
export function buildGroups(
  rows: InvestorHolding[],
  lens: PositionLens,
  facts: RowFacts
): LedgerGroup[] {
  const groups = new Map<string, LedgerGroup>();
  for (const holding of rows) {
    const id = segmentIdFor(holding, lens, facts);
    const usd = usdOf(facts, holding);
    const share = shareOf(facts, holding) ?? 0;
    const existing = groups.get(id);
    if (existing) {
      existing.value += usd ?? 0;
      existing.valuedCount += usd != null ? 1 : 0;
      existing.share += share;
      existing.members.push(holding);
    } else {
      groups.set(id, {
        id,
        label: groupLabelFor(holding, lens),
        value: usd ?? 0,
        valuedCount: usd != null ? 1 : 0,
        share,
        members: [holding]
      });
    }
  }
  const list = [...groups.values()].sort(
    (left, right) => right.value - left.value
  );
  for (const group of list) {
    // Rows with no dollar value sink, whatever the reason.
    group.members.sort(
      (left, right) => (usdOf(facts, right) ?? -1) - (usdOf(facts, left) ?? -1)
    );
  }
  return list;
}

/**
 * What the footer says when the ledger stopped short of every ROWA position,
 * or nothing. The old 100-row cap is gone (v1.31.0): the ledger traverses up
 * to its safety ceiling and reports how far it got in the warning's context
 * as { included, totalPositions }. Say the served numbers when present.
 */
export function positionsTruncationNote(
  holdings: InvestorHoldingsResponse | undefined
): string {
  const warning = holdings?.warnings.find(
    (entry) => entry.code === 'ROWA_POSITIONS_TRUNCATED'
  );
  if (!warning) return '';
  const included = warning.context?.included;
  const total = warning.context?.totalPositions;
  return typeof included === 'number' && typeof total === 'number'
    ? ` Showing the ${included} positions the ledger could traverse of ${total} total.`
    : ' The ledger could not traverse every ROWA position; more may exist beyond this set.';
}

/** A copy of `ids` with `id` added, or taken out when it was there. */
export function toggleId(
  ids: ReadonlySet<string>,
  id: string
): ReadonlySet<string> {
  const next = new Set(ids);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
}
